import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { getIntlLocale } from '@/components/common/translateFunctions';
import { VALUE_AXIS_SCALES_BY_GROUP } from '@/features/time-series-chart/config/valueAxisConfig';
import type { TimeSeriesChartModel } from '@/features/time-series-chart/types/TimeSeriesChartModel.types';
import {
  type TimeRange,
  type TimeSeriesChartAccessibility,
  type TimeSeriesChartNavigation,
  type TimeSeriesChartSectionLabels,
  TimeSeriesChart,
} from '@/shared/charts';

import { TimeSeriesChartErrorState } from '../time-series-chart-error-state/TimeSeriesChartErrorState';
import { ChartCanvas, ChartViewRoot, RefreshProgress } from './TimeSeriesChartView.style';

export type TimeSeriesChartViewProps = {
  chart: TimeSeriesChartModel;
  timeDomain: TimeRange;
  /** The line series shown as the navigation slider's shadow preview; see TimeSeriesChartNavigation. */
  previewSeriesId: string | undefined;
  isRefreshing: boolean;
  hasRefreshError: boolean;
  onRetry: () => void;
};

/**
 * The chart stays mounted while new data loads; a thin progress bar is the only feedback, so
 * ECharts can merge the changed series instead of being re-created. A failed refresh keeps the
 * previous result on screen and only adds the retry above it.
 *
 * Every object handed to the chart keeps its reference until its content changes: the chart
 * rebuilds its whole ECharts option whenever one of them is new.
 */
export function TimeSeriesChartView({
  chart,
  timeDomain,
  previewSeriesId,
  isRefreshing,
  hasRefreshError,
  onRetry,
}: TimeSeriesChartViewProps) {
  const { t, i18n } = useTranslation();

  const lineSeriesCount = chart.series.length;
  const laneCount = chart.rangeTrack?.lanes.length ?? 0;

  const navigation = useMemo<TimeSeriesChartNavigation>(() => ({ previewSeriesId }), [previewSeriesId]);

  const sectionLabels = useMemo<TimeSeriesChartSectionLabels>(
    () => ({
      plot: `${t('timeSeriesChart.channelSelector.groups.channels')} (${lineSeriesCount})`,
      ...(laneCount > 0 && { rangeTrack: `${t('timeSeriesChart.chart.investigatedValues')} (${laneCount})` }),
    }),
    [t, lineSeriesCount, laneCount]
  );

  const accessibility = useMemo<TimeSeriesChartAccessibility>(
    () => ({
      label: t('timeSeriesChart.chart.accessibilityLabel'),
      description: t('timeSeriesChart.chart.accessibilityDescription'),
    }),
    [t]
  );

  return (
    <ChartViewRoot>
      {hasRefreshError && (
        <TimeSeriesChartErrorState
          message={t('timeSeriesChart.chart.error')}
          isRetrying={isRefreshing}
          onRetry={onRetry}
          data-qa-element-id="time-series-chart-refresh-error"
        />
      )}
      <ChartCanvas data-qa-element-id="time-series-chart-canvas">
        {isRefreshing && <RefreshProgress aria-label={t('timeSeriesChart.chart.loading')} />}
        <TimeSeriesChart
          series={chart.series}
          rangeTrack={chart.rangeTrack}
          highlight={chart.highlight}
          timeDomain={timeDomain}
          navigation={navigation}
          isTooltipEnabled
          accessibility={accessibility}
          sectionLabels={sectionLabels}
          valueAxisScales={VALUE_AXIS_SCALES_BY_GROUP}
          locale={getIntlLocale(i18n.language)}
        />
      </ChartCanvas>
    </ChartViewRoot>
  );
}
